export interface ArticleSection {
  type: 'heading' | 'paragraph' | 'image';
  content: string;
  level?: number;
}

export interface Article {
  id: number;
  comicId: number;
  title: string;
  author: string;
  category: string;
  coverImage: string;
  publishDate: string;
  updateDate: string;
  views: number;
  rating: number;
  description: string;
  sections: ArticleSection[];
  tags: string[];
  relatedComicIds: number[];
}

const longDescription = `在浩瀚的修真世界中，每个人都在追寻着属于自己的道。有的人追求力量，有的人追求长生，而更多的人则在迷茫中探索着前行的方向。本作品以其宏大的世界观和细腻的人物刻画，为读者展现了一个瑰丽多彩的修真宇宙。

故事的主人公从最底层开始，凭借坚韧不拔的意志和过人的天赋，一步步突破自我，在修真之路上不断前行。他遇到了形形色色的人物——有亦师亦友的前辈，有势均力敌的对手，也有默默支持的伙伴。每一次战斗、每一次突破，都在书写着属于他自己的传奇。

作者以精湛的画功和出色的分镜技巧，将战斗场面的震撼力展现得淋漓尽致。每一个招式都有其独特的视觉表现，每一场对决都让人热血沸腾。同时，作品中也不乏温情时刻，人物之间的情感羁绊贯穿始终。

作为一部现象级的漫画作品，它不仅在国内拥有庞大的读者群体，在海外也收获了极高的评价。如果你还没有开始阅读这部作品，那么现在就是最好的时机——跟随着主角的脚步，一起踏上这场波澜壮阔的冒险之旅吧！`;

export const articles: Article[] = [
  {
    id: 1,
    comicId: 1,
    title: '星辰变：从凡人到绝世强者的修真之路',
    author: '我吃西红柿',
    category: 'xuanhuan',
    coverImage: 'https://readdy.ai/api/search-image?query=A%20breathtaking%20panoramic%20xianxia%20landscape%20with%20floating%20celestial%20palaces%20among%20golden%20clouds%20and%20starry%20night%20sky%2C%20mystical%20energy%20flowing%20through%20the%20scene%2C%20warm%20golden%20and%20amber%20tones%2C%20epic%20cinematic%20composition&width=1200&height=600&seq=article-hero-01&orientation=landscape',
    publishDate: '2026-08-01',
    updateDate: '2026-08-04',
    views: 12800000,
    rating: 9.5,
    description: '一名普通少年意外获得星辰之力传承，从此踏上修真之路。在浩瀚宇宙中，他历经无数磨难，从弱小的凡人一步步成长为掌控星辰的绝世强者。本文将带你深入了解这部现象级漫画的精彩之处。',
    sections: [
      { type: 'heading', content: '故事背景与世界观', level: 2 },
      { type: 'paragraph', content: '《星辰变》构建了一个极其宏大的修真宇宙。在这个世界中，修真者通过吸收天地灵气、感悟法则之力来提升自己的修为境界。从炼气期、筑基期、金丹期，一直到元婴期、化神期，每一个境界都代表着对天地法则的不同理解层次。' },
      { type: 'paragraph', content: '与众不同的是，这部作品引入了"星辰之力"这一独特的力量体系。主角秦羽意外获得的星辰变功法，让他能够直接沟通宇宙中的星辰能量，这为他后来成为绝世强者奠定了独一无二的基础。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=A%20majestic%20fantasy%20cultivation%20world%20with%20towering%20jade%20mountains%2C%20ancient%20temples%20floating%20in%20the%20sky%2C%20and%20glowing%20celestial%20energy%20spiraling%20through%20the%20air%2C%20warm%20ethereal%20golden%20light%2C%20epic%20scale&width=1200&height=600&seq=article-section-01&orientation=landscape' },
      { type: 'heading', content: '主角成长与人物塑造', level: 2 },
      { type: 'paragraph', content: '秦羽的成长历程是本作最核心的魅力所在。他从一个被家族轻视的普通少年开始，凭借着不屈不挠的意志和对力量的渴望，一步步突破自我极限。在这个过程中，他不仅提升了修为，更重要的是心智的成熟与蜕变。' },
      { type: 'paragraph', content: '作者在人物塑造上非常用心。每个配角都有自己独特的性格和故事线，没有一个是简单的工具人。无论是秦羽的师父、同门师兄弟，还是后来的对手和盟友，都为整个故事增添了丰富的层次感和真实感。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=A%20young%20cultivator%20training%20in%20a%20misty%20bamboo%20forest%20with%20spiritual%20energy%20swirling%20around%2C%20ancient%20stone%20tablets%20with%20carved%20techniques%20nearby%2C%20warm%20sunlight%20filtering%20through%20leaves%2C%20serene%20atmosphere&width=1200&height=600&seq=article-section-02&orientation=landscape' },
      { type: 'heading', content: '战斗系统与视觉表现', level: 2 },
      { type: 'paragraph', content: '《星辰变》的战斗系统设计精妙绝伦。每一种功法都有其独特的视觉表现，而星辰变功法的战斗画面更是令人叹为观止。当秦羽催动星辰之力时，整个画面都会被璀璨的星光所覆盖，给读者带来震撼的视觉享受。' },
      { type: 'paragraph', content: '漫画家在分镜设计上也下了很大功夫。大场面战斗的分镜极具张力，动作流畅而有力；而内心戏的刻画则细腻入微，一个眼神、一个微表情都能传递出丰富的情感信息。' },
      { type: 'heading', content: '为什么推荐阅读？', level: 2 },
      { type: 'paragraph', content: longDescription },
    ],
    tags: ['玄幻', '修真', '热血', '推荐'],
    relatedComicIds: [9, 7, 5],
  },
  {
    id: 2,
    comicId: 7,
    title: '烈焰战神：最燃热血战斗漫画深度解析',
    author: '火云邪神',
    category: 'rexue',
    coverImage: 'https://readdy.ai/api/search-image?query=An%20epic%20battle%20scene%20with%20two%20warriors%20clashing%20amidst%20explosions%20of%20fire%20and%20energy%2C%20dramatic%20lighting%20with%20intense%20red%20and%20orange%20flames%2C%20cinematic%20action%20composition%2C%20powerful%20energy%20waves&width=1200&height=600&seq=article-hero-02&orientation=landscape',
    publishDate: '2026-07-28',
    updateDate: '2026-08-03',
    views: 15600000,
    rating: 9.6,
    description: '上古烈焰战神陨落千年后，少年叶炎意外获得其火种传承。本文将解析这部评分最高的热血战斗漫画，探索其独特的战斗系统和人物魅力。',
    sections: [
      { type: 'heading', content: '九大火种：独特的力量体系', level: 2 },
      { type: 'paragraph', content: '《烈焰战神》最引人注目的设计就是"九大火种"的力量体系。上古烈焰战神陨落后，其力量化为九枚不同属性的火种散落人间。每一枚火种都蕴含着不同方向的火焰力量——有焚尽万物的毁灭之炎，也有涅槃重生的生命之炎。' },
      { type: 'paragraph', content: '主角叶炎获得的是第一枚火种——"初始之炎"，虽然看似最弱，但它拥有兼容其他火种的特殊能力。这为后续剧情的发展埋下了精彩的伏笔，也让读者对他的成长充满期待。' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=Nine%20distinct%20flames%20floating%20in%20a%20circular%20formation%2C%20each%20with%20different%20colors%20from%20crimson%20to%20golden%20to%20azure%2C%20surrounded%20by%20ancient%20runes%2C%20mystical%20atmosphere%2C%20warm%20glowing%20light&width=1200&height=600&seq=article-section-03&orientation=landscape' },
      { type: 'heading', content: '战斗场面：视觉盛宴', level: 2 },
      { type: 'paragraph', content: '作为一部热血战斗漫画，战斗场面自然是重中之重。本作的每一场战斗都经过精心设计，从招式名称到视觉表现都极具创意。叶炎的"炎拳"系列招式随着火种数量的增加不断升级，每次新的战斗都让读者眼前一亮。' },
      { type: 'paragraph', content: '特别值得称赞的是战斗中的节奏把控。作者懂得在激烈战斗后安排适当的喘息时间，让角色的情感得到充分展现，避免了纯打斗带来的视觉疲劳。' },
      { type: 'heading', content: '角色魅力：不止于战斗', level: 2 },
      { type: 'paragraph', content: '《烈焰战神》的角色群像塑造同样出色。叶炎虽然天赋异禀，但并非一帆风顺的开挂型主角。他在成长过程中经历了无数挫折和失败，正是这些经历让他变得更加坚韧和强大。' },
      { type: 'paragraph', content: '配角阵容同样亮眼。亦敌亦友的"冰霜剑客"、神秘莫测的"影之炎"持有者、温柔坚强的青梅竹马……每个角色都鲜活立体，让整个故事充满了张力与温度。如果你正在寻找一部让你热血沸腾的漫画，这部绝对不容错过！' },
    ],
    tags: ['热血', '战斗', '冒险', '推荐'],
    relatedComicIds: [1, 5, 11],
  },
  {
    id: 3,
    comicId: 12,
    title: '仙王的日常生活：最强修仙者的搞笑校园日常',
    author: '枯玄',
    category: 'gaoxiao',
    coverImage: 'https://readdy.ai/api/search-image?query=A%20powerful%20immortal%20in%20traditional%20Chinese%20robes%20sitting%20in%20a%20modern%20classroom%20surrounded%20by%20confused%20students%2C%20humorous%20contrast%20between%20ancient%20and%20modern%2C%20bright%20warm%20sunlight%2C%20comedy%20atmosphere&width=1200&height=600&seq=article-hero-03&orientation=landscape',
    publishDate: '2026-07-20',
    updateDate: '2026-08-02',
    views: 18900000,
    rating: 9.7,
    description: '修仙界最强仙王为了躲避工作跑到凡间上高中，奈何实力太强根本低调不了！本文带你领略这部全网评分最高的搞笑漫画的独特魅力。',
    sections: [
      { type: 'heading', content: '反差萌：仙王也要上学？', level: 2 },
      { type: 'paragraph', content: '想象一下，一个活了不知道多少年的仙王，每天要像普通高中生一样早起上课、参加考试、被老师点名回答问题……这种强烈的反差感是《仙王的日常生活》最核心的笑点来源。' },
      { type: 'paragraph', content: '主角王令（仙王在凡间的化名）虽然封印了大部分记忆和力量，但偶尔不经意的"仙王本能"总会引发各种令人啼笑皆非的状况。比如考试时下意识用神识偷看答案（然后发现全班同学的水平都不如他）、体育课时差点用轻功打破世界纪录……' },
      { type: 'image', content: 'https://readdy.ai/api/search-image?query=A%20humorous%20scene%20of%20a%20divine%20being%20struggling%20with%20mundane%20school%20tasks%2C%20like%20carrying%20a%20stack%20of%20homework%20while%20subtle%20spiritual%20energy%20leaks%20out%2C%20bright%20comedy%20style%2C%20warm%20colors&width=1200&height=600&seq=article-section-04&orientation=landscape' },
      { type: 'heading', content: '角色群体：群像喜剧', level: 2 },
      { type: 'paragraph', content: '除了主角王令之外，他身边的一众同学也是笑点制造机。有对他莫名崇拜的中二病同学、有暗恋他的学妹（后来发现是某个修仙门派的圣女转世）、还有总是试图揭穿他"真面目"的班长。' },
      { type: 'paragraph', content: '每个角色都有自己的故事线和成长轨迹，他们在王令潜移默化的影响下，各自收获了友谊、勇气和成长。这种将搞笑与温情结合的叙事方式，让整部作品超越了单纯的搞笑漫画范畴。' },
      { type: 'heading', content: '修仙元素与现代生活的碰撞', level: 2 },
      { type: 'paragraph', content: '作品最精妙的设计在于将修仙世界的各种设定无缝融入到现代校园生活中。比如：体育课变成了"炼体术修炼"，化学实验变成了"炼丹术入门"，学生会竞选变成了"宗门大比"……' },
      { type: 'paragraph', content: '这种巧妙的"文化碰撞"不仅制造了源源不断的笑点，也让读者在欢笑中感受到传统文化与现代生活的有趣交融。如果你需要一部能让你开怀大笑的漫画，那就是它了！' },
    ],
    tags: ['搞笑', '修仙', '校园', '推荐'],
    relatedComicIds: [6, 4, 8],
  },
];

export const getArticleById = (id: number): Article | undefined => {
  return articles.find((article) => article.id === id);
};

export const getArticlesByComicId = (comicId: number): Article[] => {
  return articles.filter((article) => article.comicId === comicId);
};